// src/components/provider/FeedbackModal.tsx
import React, { useState } from 'react';
import { useRouter } from "next/navigation";
import { X, Handshake, User, ArrowRight, ExternalLink } from 'lucide-react'; 
import { Provider, Rating } from '@/store';
import { ratingApi } from '@/api';
import { calculateRatingStats } from '@/lib/ratingUtils';
import { Button } from "@/components/ui/button"; // Optional if you have this, otherwise standard buttons work

interface FeedbackModalProps {
  provider: Provider;
  onClose: () => void;
  onReviewSubmit: (newRating: Rating) => void;
}

export function FeedbackModal({ provider, onClose, onReviewSubmit }: FeedbackModalProps) {
  const router = useRouter(); 
  const [activeTab, setActiveTab] = useState<'ratings' | 'endorsements'>('ratings');
  
  // Rating Form State
  const [selectedRating, setSelectedRating] = useState<'p' | 'p+' | 'p++' | 'p*' | null>(null);
  const [reviewText, setReviewText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const ratingStats = calculateRatingStats(provider.ratings || []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRating) {
      setError('Please select a rating');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      const response = await ratingApi.create({
        provider_id: provider.id,
        rating_value: selectedRating,
        review_text: reviewText
      });
      onReviewSubmit(response.rating);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to submit review');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRatingButtonClass = (ratingValue: 'p' | 'p+' | 'p++' | 'p*') => {
    const isSelected = selectedRating === ratingValue;
    switch (ratingValue) {
      case 'p': return isSelected ? "bg-red-500 text-white shadow-md ring-2 ring-red-300" : "bg-white text-red-600 border border-red-200 hover:bg-red-50";
      case 'p+': return isSelected ? "bg-orange-500 text-white shadow-md ring-2 ring-orange-300" : "bg-white text-orange-600 border border-orange-200 hover:bg-orange-50";
      case 'p++': return isSelected ? "bg-blue-500 text-white shadow-md ring-2 ring-blue-300" : "bg-white text-blue-600 border border-blue-200 hover:bg-blue-50";
      case 'p*': return isSelected ? "bg-green-500 text-white shadow-md ring-2 ring-green-300" : "bg-white text-green-600 border border-green-200 hover:bg-green-50";
      default: return "";
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        
        {/* Header */}
        <div className="flex justify-between items-start p-5 border-b bg-white z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900 truncate max-w-[200px] leading-tight">
              {provider.business_name}
            </h2>
            <div className="flex gap-6 mt-4">
              <button 
                onClick={() => setActiveTab('ratings')}
                className={`text-sm font-semibold pb-2 transition-all border-b-2 ${
                  activeTab === 'ratings' ? 'text-blue-600 border-blue-600' : 'text-gray-400 border-transparent hover:text-gray-600'
                }`}
              >
                Ratings
              </button>
              <button 
                onClick={() => setActiveTab('endorsements')}
                className={`text-sm font-semibold pb-2 transition-all border-b-2 ${
                  activeTab === 'endorsements' ? 'text-green-600 border-green-600' : 'text-gray-400 border-transparent hover:text-gray-600'
                }`}
              >
                Endorsed By ({provider.trust_count || 0})
              </button>
            </div>
          </div>
          <button onClick={onClose} className="p-2 -mr-2 -mt-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto p-5 custom-scrollbar bg-gray-50/50 flex-1">
          
          {/* RATINGS TAB */}
          {activeTab === 'ratings' && (
            <div className="space-y-6 animate-in slide-in-from-left-4 duration-300">
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: 'Basic', count: ratingStats.p, color: 'text-red-600', bg: 'bg-red-50 border-red-100' },
                  { label: 'Good', count: ratingStats['p+'], color: 'text-orange-600', bg: 'bg-orange-50 border-orange-100' },
                  { label: 'V.Good', count: ratingStats['p++'], color: 'text-blue-600', bg: 'bg-blue-50 border-blue-100' },
                  { label: 'Excel.', count: ratingStats['p*'], color: 'text-green-600', bg: 'bg-green-50 border-green-100' },
                ].map((stat) => (
                  <div key={stat.label} className={`${stat.bg} p-2.5 rounded-xl text-center border shadow-sm`}>
                    <div className={`text-xl font-black ${stat.color}`}>{stat.count}</div>
                    <div className={`text-[10px] uppercase font-bold ${stat.color} opacity-80 mt-1`}>{stat.label}</div>
                  </div>
                ))}
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-5 pt-2">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">Select Your Rating</label>
                  <div className="grid grid-cols-4 gap-2">
                    {(['p', 'p+', 'p++', 'p*'] as const).map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setSelectedRating(r)}
                        className={`py-3 rounded-lg text-sm font-bold transition-all transform active:scale-95 ${getRatingButtonClass(r)}`}
                      >
                        {r.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label htmlFor="reviewText" className="block text-sm font-bold text-gray-700 mb-2">Review (Optional)</label>
                  <textarea
                    id="reviewText"
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 min-h-[100px] resize-none shadow-sm bg-white"
                    placeholder="Share your experience..."
                  />
                </div>
                {error && <div className="text-red-600 text-xs font-medium bg-red-50 p-3 rounded-lg border border-red-100">{error}</div>}
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={onClose} className="flex-1 py-3 border border-gray-200 bg-white rounded-xl font-semibold text-sm hover:bg-gray-50 transition-colors text-gray-700">Cancel</button>
                  <button type="submit" disabled={isSubmitting} className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-70 transition-colors shadow-md shadow-blue-200">
                    {isSubmitting ? 'Submitting...' : 'Submit Review'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ENDORSEMENTS TAB */}
          {activeTab === 'endorsements' && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              
              {/* Stats Card */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 text-center shadow-sm">
                <div className="bg-white w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 shadow-md border border-green-100">
                  <Handshake className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="text-2xl font-black text-green-800 tracking-tight">
                  {provider.trust_count || 0} People
                </h3>
                <p className="text-sm text-green-700 font-medium">know and trust this provider</p>
              </div>

              <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2 mb-4">
                  Recent Supporters
                </h4>
                
                {provider.trusted_by && provider.trusted_by.length > 0 ? (
                  <div className="space-y-3">
                    {provider.trusted_by.map((user: any) => (
                      // ✅ CARD STYLE: Similar to ProfileCompletionCard
                      <div 
                        key={user.id} 
                        onClick={() => {
                          if (user.provider_id) {
                            onClose();
                            router.push(`/providers/detail?id=${user.provider_id}`);
                          }
                        }}
                        className={`
                          group relative rounded-xl border border-border p-4 shadow-sm transition-all duration-200
                          ${user.provider_id 
                            ? "bg-white hover:border-blue-300 hover:shadow-md cursor-pointer" 
                            : "bg-gray-50/50 border-gray-100"
                          }
                        `}
                      >
                        <div className="flex items-center justify-between gap-4">
                          
                          <div className="flex items-center gap-3.5">
                            {/* Avatar */}
                            <div className={`
                              h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold shadow-sm
                              ${user.provider_id ? "bg-blue-100 text-blue-700" : "bg-gray-200 text-gray-500"}
                            `}>
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            
                            <div>
                              <h5 className={`font-semibold text-sm ${user.provider_id ? "text-gray-900 group-hover:text-blue-700" : "text-gray-600"}`}>
                                {user.name}
                              </h5>
                              
                              {/* Badge or Subtext */}
                              {user.provider_id ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100 mt-1">
                                  Verified Provider
                                </span>
                              ) : (
                                <p className="text-[11px] text-gray-400 mt-0.5">Community Member</p>
                              )}
                            </div>
                          </div>

                          {/* Action Icon (Only for providers) */}
                          {user.provider_id && (
                            <div className="h-8 w-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                              <ExternalLink className="h-4 w-4" />
                            </div>
                          )}

                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                    <p className="text-sm text-gray-500 font-medium">No endorsements yet.</p>
                    <p className="text-xs text-gray-400 mt-1">Be the first to endorse!</p>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
